import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { setRequestedURL } from "../util/UrlHelper";

const AppUrlListener: React.FC<any> = () => {
  const history = useHistory();

  useEffect(() => {
    // Dedupe so a cold-start URL surfaced by BOTH getLaunchUrl and appUrlOpen
    // is only routed once.
    let lastRoutedUrl: string | undefined;

    // Route an incoming deep-link URL to its in-app path. Shared by the
    // cold-start (getLaunchUrl) and warm (appUrlOpen) paths.
    const routeFromUrl = async (url?: string) => {
      if (!url || url === lastRoutedUrl) return;
      // OAuth / MSAL callbacks are handled by the auth flow, not routed here.
      if (
        url.startsWith("com.aptean.eam://oauth/auth") ||
        url.startsWith("msauth.com.aptean.eam://auth")
      ) {
        return;
      }
      // Universal Link / App Link (e.g. a shared work-order deep link,
      // {host}/workorder/{id}/details): route to the URL's path so the app
      // opens directly on that screen. Parse via URL so it works regardless of
      // the host's TLD (the old split(".com") broke on non-.com hosts).
      let slug: string | undefined;
      try {
        const parsed = new URL(url);
        slug = `${parsed.pathname}${parsed.search}`;
      } catch {
        // Fallback for non-standard URLs: take everything after the host.
        slug = url.split(".com").pop();
      }
      if (slug && slug !== "/") {
        lastRoutedUrl = url;
        console.log("App URL Listener - SLUG: ", slug);
        // Persist the target so it survives the auth flow. On cold start the
        // sign-in path does a full-page redirect (window.location.href ->
        // /AuthCallback), which discards this in-app push and otherwise lands
        // on Home; AuthCallback restores it via getRequestedURL() after login.
        // Mirrors the QR-code launch flow (QRCodeService.setRequestedURL).
        await setRequestedURL(slug);
        history.push(slug);
      }
      // If no usable path, do nothing - let regular routing logic take over
    };

    // Cold start: the app was launched by tapping a deep link. The launch URL
    // is delivered before this listener attaches, so appUrlOpen misses it —
    // read it explicitly here.
    App.getLaunchUrl()
      .then((launch) => routeFromUrl(launch?.url))
      .catch(() => {});

    // Warm: the app is already running when a deep link is opened.
    const listenerHandle = App.addListener(
      "appUrlOpen",
      (event: URLOpenListenerEvent) => routeFromUrl(event.url)
    );

    return () => {
      listenerHandle.then((handle) => handle.remove()).catch(() => {});
    };
  }, [history]);

  return null;
};

export default AppUrlListener;
